import React, { FC, Fragment, useEffect, useState, useContext } from "react"
import ConnectCalendarWarningCardModal from "../ConnectCalendarWarningCardModal"
import CustomDayPicker from "../date-time/CustomDayPicker"
import ForceVerifyEmail from "../../pages/ForceVerifyEmail"
import Loader from "../ui/loader"
import moment from "moment"
import secureLocalStorage from "react-secure-storage"
import TimeOptions from "../scheduling/new-meeting/step-views/TimeOptions"
import useLoading from "../../hooks/useLoading"
import { add } from "date-fns"
import { AvailabilityRulesData } from "../../types/availabilityTypes"
import { Button } from "../ui/button"
import { CalendarRequests, ServiceRequests } from "../../api/app.service"
import { cn } from "../../utils/cn"
import { CoachPublicProfileContext } from "../../pages/manage-business/profile/context/coachPublicProfileContext"
import { CombinedConsultationForm } from "../auth/register-form/combined-consultation-form"
import { ContactRequests } from "../../api/app.service"
import { FreeConsultationConfirm } from "../services/FreeConsultationConfirm"
import { GetAvailability } from "../../models/app.interface"
import { isCoachInCommissionTracking } from "../../utils/commissionTracking"
import { getLightTextColor } from "../../utils/getLightTextColor"
import { getTextColor } from "../../utils/getTextColor"
import { RegisterFormProvider } from "../auth/register-form/context"
import { useAuthenticatedUser } from "../../hooks/useAuthenticatedUser"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  consultation?: any
  disabled?: boolean
}

const ProfileSchedulerPopup: FC<Props> = ({
  consultation,
  disabled = false,
}) => {
  const { user, setUser } = useContext(UserCtx)
  const navigate = useNavigate()
  const [activeDate, setActiveDate] = useState<Date>(new Date())
  const { coachPublicProfileData, isOwner } = useContext(
    CoachPublicProfileContext
  )
  const [clientServices, setClientServices] = useState<any[]>([])
  const [servicesLoading, setServicesLoading] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [availablePeriods, setAvailablePeriods] = useState<Array<any>>([])
  const [freeConsultConfirmModal, setFreeConsultConfirmModal] =
    useState<boolean>(false)
  const [combinedConsultationForm, setCombinedConsultationForm] =
    useState<boolean>(false)
  const [showForceVerifyModal, setShowForceVerifyModal] = useState(false)
  const [showConnectCalendar, setShowConnectCalendar] = useState<any>(false)
  const [userHasCalendar, setUserHasCalendar] = useState<boolean>(false)
  const [userCalendars, setUserCalendars] = useState<any>({
    profileCount: 0,
    calendars: {},
  })
  const [bookingWindow, setBookingWindow] = useState<number>(0)

  const [userAvailabilities, setUserAvailabilities] = useState<
    Array<AvailabilityRulesData>
  >([])
  const [loadingAvailablePeriods, setLoadingAvailablePeriods] =
    useState<boolean>(false)
  const { startLoading, stopLoading } = useLoading()
  const { toast } = useToast()
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [endTime, setEndTime] = useState<Date | null>(null)
  const authenticatedUser = useAuthenticatedUser(user)
  const coachColor = coachPublicProfileData?.profile_information?.profileColor
  const textColor = getLightTextColor(
    coachPublicProfileData?.profile_information?.profileColor
  )
  const [showCalendarNotice, setShowCalendarNotice] = useState<any>(false)
  const localTimeZone = moment.tz.guess()
  const timezoneAbbreviation = moment.tz(localTimeZone).format("z")

  const fullTimezoneName = new Intl.DateTimeFormat("en-US", {
    timeZone: localTimeZone,
    timeZoneName: "long",
  })
    .formatToParts(new Date())
    .find((part) => part.type === "timeZoneName")?.value

  const durationDefault = 60
  const duration = consultation?.service_details?.session?.duration
    ? parseInt(consultation?.service_details?.session?.duration) / 60
    : 60

  useEffect(() => {
    if (coachPublicProfileData) {
      getProfileServiceList()
    }
  }, [coachPublicProfileData])

  const getProfileServiceList = () => {
    if (user && coachPublicProfileData) {
      setServicesLoading(true)
      const coachProfileId = parseInt(coachPublicProfileData.coach_profile_id)
      const memberProfileId = parseInt(user.activeProfileId)
      ServiceRequests.getServiceListProfile({
        coach_profile_id: coachProfileId,
        member_profile_id: memberProfileId,
      })
        .then((data) => {
          if (data && data.service_list) {
            setClientServices(data.service_list)
          } else {
            setClientServices([])
          }
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response?.data?.message || "An error occurred")
        })
        .finally(() => {
          setServicesLoading(false)
        })
    } else {
      console.warn("User or Coach Public Profile Data is missing.")
    }
  }

  const getConsultationAvailabilityId = () => {
    let activeAvailabilityIdArr: Array<string> = []

    const activeConsultationAvailability = userAvailabilities.find(
      (availability) => {
        return (
          availability.id ===
          consultation?.service_details?.availability_rule_id
        )
      }
    )
    const defaultAvailability = userAvailabilities.find(
      (availability) => availability.is_default
    )

    const firstAvailability = userAvailabilities[0]

    if (activeConsultationAvailability?.id) {
      activeAvailabilityIdArr.push(activeConsultationAvailability.id.toString())
    } else if (defaultAvailability?.id) {
      activeAvailabilityIdArr.push(defaultAvailability.id.toString())
    } else if (firstAvailability?.id) {
      activeAvailabilityIdArr.push(firstAvailability.id.toString())
    }

    return activeAvailabilityIdArr
  }

  const getAvailability = () => {
    setLoadingAvailablePeriods(true)
    const profileIds = [coachPublicProfileData?.coach_profile_id]

    let now = moment.utc()
    let active_date = moment(activeDate)
    let start_date: moment.Moment = active_date

    if (active_date.isSame(now, "day")) {
      // For non-creators on today's date, the booking window pushes the effective date to tomorrow
      // So we should request availability for tomorrow, not today with a time offset
      start_date = now.clone().add(bookingWindow, "hours")
    } else {
      start_date = active_date.clone().startOf("day")
    }
    console.log("SD: ", start_date)
    const finalStartDate = start_date.toISOString()

    const startMoment = moment.utc(finalStartDate)

    const end = startMoment.clone().tz(localTimeZone).endOf("day").toISOString()

    const getAvailabilityRequest: GetAvailability = {
      profile_ids: profileIds,
      creator_profile_id: parseInt(coachPublicProfileData?.coach_profile_id),
      availability_rule_ids: getConsultationAvailabilityId(),
      meeting_duration: duration || durationDefault,
      start_date: finalStartDate,
      end_date: end,
      coach_user_id: parseInt(coachPublicProfileData?.user_id),
      show_all_times: true,
    }

    CalendarRequests.getAvailability(getAvailabilityRequest)
      .then((data) => {
        setAvailablePeriods(data.available_periods)
        setLoadingAvailablePeriods(false)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
        setLoadingAvailablePeriods(false)
      })
  }

  const getAvailabilityRules = async () => {
    await CalendarRequests.getAvailabilityRules({
      user_id: parseInt(coachPublicProfileData?.user_id),
    })
      .then((data) => {
        setUserAvailabilities(data.availability_rules)
        const matchingAvailability = data.availability_rules.find(
          (rule: any) =>
            rule.id === consultation?.service_details?.availability_rule_id
        )
        if (matchingAvailability) {
          setBookingWindow(matchingAvailability.min_book_window / 60)
        } else {
          console.warn("No matching availability found")
        }
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
      })
  }

  const showFreeConsultConfirm = () => {
    setFreeConsultConfirmModal(true)
  }

  const sendToConsultationForm = () => {
    if (startTime && endTime) {
      const bookingDetails = {
        start: startTime,
        end: endTime,
      }

      navigate(
        `/${coachPublicProfileData?.endpoint_slug}/booking/${consultation?.service_id}`,
        { state: bookingDetails }
      )
    }
  }

  const connectAndRedirect = async (user: any) => {
    try {
      localStorage.setItem(
        "meetingToSchedule",
        JSON.stringify({ startTime: startTime, endTime: endTime })
      )

      if (user) {
        await ContactRequests.createContactConnection({
          sender_profile_id: coachPublicProfileData?.coach_profile_id,
          receiver_profile_id: user.activeProfileId,
          user_environment: process.env.REACT_APP_USER_ENVIRONMENT || "",
          status: isCoachInCommissionTracking(
            coachPublicProfileData?.endpoint_slug
          )
            ? "zoee lead"
            : "connected",
        })

        if (consultation?.service_details?.billing?.amount === "0") {
          showFreeConsultConfirm()
        } else {
          navigate(
            `/${user.activeProfile}/${coachPublicProfileData?.endpoint_slug}/services/${consultation?.service_id}`
          )
        }

        if (!user.emailVerified) {
          setShowForceVerifyModal(true)
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        }
      } else {
        toast.error("User or user's member profile is undefined")
        stopLoading()
      }
    } catch (ex: any) {
      toast.error(ex.response?.data?.message || "An error occurred")
      stopLoading()
    }
  }

  const goToEnroll = (user: any) => {
    localStorage.setItem(
      "meetingToSchedule",
      JSON.stringify({ startTime: startTime, endTime: endTime })
    )

    if (isOwner) {
      toast.error(
        "Can't take action on profile page when logged into this account"
      )
      return
    }

    connectAndRedirect(user)
  }

  const fetchUserCalendars = () => {
    if (user) {
      CalendarRequests.getCalendarList({
        user_id: parseInt(user.user_id),
      })
        .then((data: any) => {
          if (data.profile_count > 0) {
            setUserCalendars({
              profileCount: data.profile_count,
              calendars: data.calendars,
            })
            setUserHasCalendar(true)
          }
        })
        .catch((ex: any) => {
          console.error(ex)
          toast.error(ex.response.data.message)
        })
    }
  }

  useEffect(() => {
    fetchUserCalendars()
  }, [user])

  const checkEmailOrRedirect = () => {
    if (!user) {
      secureLocalStorage.setItem(
        "coachDetails",
        JSON.stringify(coachPublicProfileData)
      )
      localStorage.setItem(
        "coachRedirect",
        `/coach/${coachPublicProfileData?.endpoint_slug}/services/${consultation?.service_id}`
      )
      localStorage.setItem(
        "memberRedirect",
        `/member/${coachPublicProfileData?.endpoint_slug}/services/${consultation?.service_id}`
      )
    }
    setIsLoading(true)
    if (user && !userHasCalendar && user.activeProfile === "coach") {
      setShowCalendarNotice(true)
      setIsLoading(false)
    } else {
      if (user && clientServices) {
        const isEnrolled = clientServices.some(
          (clientService: any) =>
            clientService.service_details.type === "consultation" &&
            clientService.service_status === "active" &&
            clientService.service_id === consultation.service_id
        )
        if (isEnrolled) {
          toast.error("You have already enrolled in this consultation.")
          setIsLoading(false)
          return
        }
      }
      if (authenticatedUser) {
        setIsLoading(false)
        goToEnroll(user)
      } else {
        if (consultation?.service_details.billing.amount === "0") {
          sendToConsultationForm()
          setIsLoading(false)
        } else {
          setCombinedConsultationForm(true)
        }
      }
    }
  }

  useEffect(() => {
    if (startTime) {
      const end = add(startTime, {
        minutes: duration || durationDefault,
      })
      setEndTime(end)
    }
  }, [startTime])

  useEffect(() => {
    if (userAvailabilities && duration && activeDate) {
      getAvailability()
    }
  }, [activeDate, userAvailabilities, duration])

  useEffect(() => {
    getAvailabilityRules()
  }, [consultation])

  useEffect(() => {
    if (!combinedConsultationForm) {
      setIsLoading(false)
    }
  }, [combinedConsultationForm])

  useEffect(() => {
    if (!freeConsultConfirmModal) {
      setIsLoading(false)
    }
  }, [freeConsultConfirmModal])

  useEffect(() => {
    if (!showCalendarNotice) {
      setIsLoading(false)
    }
  }, [showCalendarNotice])

  const invisibleCoaches =
    coachPublicProfileData?.coach_profile_id === 2002014 ||
    coachPublicProfileData?.coach_profile_id === 2002458

  return (
    <>
      {!invisibleCoaches && (
        <>
          {showCalendarNotice && !showConnectCalendar && (
            <ConnectCalendarWarningCardModal
              setShowCalendarNotice={setShowCalendarNotice}
            />
          )}
          <div
            className={cn(
              "main flex h-full max-h-[601px] min-h-[601px] min-w-[300px] flex-col rounded-xl bg-white shadow md:max-w-[300px]",
              {
                "pointer-events-none opacity-25": disabled,
              }
            )}
          >
            <div
              className="-mb-[8px] flex h-[20px] items-center justify-center rounded-t-[16px] px-[24px] text-[16px] font-bold"
              style={{
                backgroundColor: `${coachPublicProfileData?.profile_information?.profileColor}`,
                color: textColor,
              }}
            ></div>
            <div className="px-2 py-[16px] sm:py-[5px]">
              <div className="-mb-[3px] pt-[7px] font-bold leading-tight mb-6 text-[15px]">
                Schedule a Consultation
                <br />
                <span
                  className={`font-bold break-words ${
                    consultation?.service_details?.title?.length > 100
                      ? "text-[16px]"
                      : "text-[20px]"
                  }`}
                  style={{
                    color: `${coachPublicProfileData?.profile_information?.profileColor}`,
                  }}
                >
                  {consultation?.service_details?.title}
                </span>
              </div>

              <div className="mb-[5px] flex w-full items-center justify-between font-bold">
                <CustomDayPicker
                  setActiveDate={setActiveDate}
                  disablePastDates={true}
                  dayColor={coachColor}
                  bookingWindow={bookingWindow}
                />
              </div>
              {/* TIME */}
              <div className="flex h-[100px] w-full flex-col gap-[8px] overflow-y-auto">
                {loadingAvailablePeriods ? (
                  <div className="px-[8px] pt-[24px] text-center font-bold">
                    <Loader />
                    <h3 className="text-center text-[16px] font-bold">
                      Loading available meeting times.
                    </h3>
                  </div>
                ) : (
                  <>
                    {consultation?.service_details?.visibility?.profile
                      ?.service === false ? (
                      <div className="mt-8 px-2 text-center text-base font-bold flex items-center justify-center">
                        This coach offers a consultation, but it’s set to
                        private. Please reach out directly if you’d like to
                        learn more.
                      </div>
                    ) : availablePeriods?.length > 0 && consultation ? (
                      <>
                        {availablePeriods?.map((availablePeriod, idx) => (
                          <Fragment key={idx}>
                            <TimeOptions
                              availabilityStart={moment(
                                availablePeriod.start
                              ).toDate()}
                              availabilityEnd={moment(
                                availablePeriod.end
                              ).toDate()}
                              duration={duration || durationDefault}
                              setStartTime={setStartTime}
                              startTime={startTime}
                              dayColor={coachColor}
                              bookingWindow={bookingWindow}
                            />
                            <div className="bg-hover text-graySlate flex items-center justify-center rounded-[10px] p-[12px] text-base font-bold last:hidden">
                              Unavailable
                            </div>
                          </Fragment>
                        ))}
                      </>
                    ) : !consultation ? (
                      <div className="h-full mb-2 px-[4px] pt-[5px] text-center text-sm font-bold flex items-center justify-center">
                        Coach has not set a consultation
                      </div>
                    ) : (
                      <div className="mb-2 px-[4px] pt-[5px] text-center text-sm font-bold">
                        No available meeting times for you and your meeting
                        participant(s) on this day. Please select another day.
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {consultation?.service_details?.visibility?.profile?.service ? (
              <>
                <div className="timezone-info">
                  <p className="w-[85%] mx-auto text-center font-bold text-sm">
                    {" "}
                    All times shown are in <br />
                    {fullTimezoneName} ( {timezoneAbbreviation})
                  </p>
                </div>
                <Button
                  onClick={checkEmailOrRedirect}
                  disabled={!startTime || !endTime || isLoading}
                  className={cn(
                    "-mt-[20px] mb-2 mt-[2px] h-[50px] w-[85%] sm:-mt-0 font-bold shadow-md mx-auto",
                    {
                      "bg-grayFlash text-black hover:bg-white":
                        !startTime || !endTime || isLoading,
                      [`bg-${coachColor} text-${getTextColor(coachColor)}`]:
                        startTime && endTime && !isLoading,
                    }
                  )}
                  style={{
                    backgroundColor: coachColor,
                    color: getTextColor(coachColor),
                  }}
                >
                  {isLoading ? <Loader /> : "Book"}
                </Button>
              </>
            ) : (
              <></>
            )}
          </div>
          {authenticatedUser &&
            freeConsultConfirmModal &&
            startTime &&
            endTime && (
              <FreeConsultationConfirm
                startTime={startTime}
                endTime={endTime}
                consultation={consultation}
                showConfirm={freeConsultConfirmModal}
                setShowConfirm={setFreeConsultConfirmModal}
              />
            )}
          {!authenticatedUser &&
            combinedConsultationForm &&
            startTime &&
            endTime && (
              <RegisterFormProvider
                defaultEmail=""
                defaultAccountType={null}
                defaultFirstName=""
                defaultLastName=""
                defaultPassword=""
                defaultPhone=""
                onSuccess={() => true}
              >
                <CombinedConsultationForm
                  startTime={startTime}
                  endTime={endTime}
                  consultation={consultation}
                  showConfirm={combinedConsultationForm}
                  setShowConfirm={setCombinedConsultationForm}
                />
              </RegisterFormProvider>
            )}
          {/* Might not need this */}
          {showForceVerifyModal && <ForceVerifyEmail />}
        </>
      )}
    </>
  )
}

export default ProfileSchedulerPopup
